import { HttpClient } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { LocalRepository } from "@shared/helper/local-repository";
import { TServiceResult } from "@shared/helper/t-service-result";
import { PageList } from "@shared/model/categoryAndSearch/pageList-model";
import { IClubJoinRequestAggregateModel } from "@app/pages/clubs/models/club-join-request-aggregate-model";
import { IClubMemberAggregateModel } from "@app/pages/clubs/models/club-member-aggregate-model";
import { Observable, catchError, of } from "rxjs";
import { HttpBaseService } from "@shared/service/base_services/http_base.service";
import { IClubCourseAggregateModel } from "@app/pages/clubs/models/club-course-aggregate-model";
import { TermFilterModel } from "@shared/model/categoryAndSearch/term-filter-model";
import { IClubPollAggregateModel } from "@app/pages/clubs/models/club-poll-aggregateModel";
import { IClubInviteAggregateModel } from "@app/pages/clubs/models/club-invite-aggregate-model";
import { IClubPreviewModel } from "../models/club-preview-model";
import { IClubWithActiveCourseModel } from "../models/club-with-active-course-model";
import { SearchFilterModel } from "@shared/model/categoryAndSearch/search-filter-model";
import { IClubCustomerModel } from "../models/club-customer-model";
import { EClubPollState } from "../models/club-poll-model";

@Injectable({
  providedIn: 'root'
})
export class ClubAggregatorService extends HttpBaseService {

  constructor() {
    const repo = inject(LocalRepository);
    const http = inject(HttpClient);

    super(repo, http, 'v1/clbAggr');
  }

  getMyClubs(filters: TermFilterModel): Observable<TServiceResult<PageList<IClubWithActiveCourseModel>>> {
    const params = this.ServiceTermFilter(filters);
    return this.http.get<TServiceResult<PageList<IClubWithActiveCourseModel>>>(`${this.apiUrl}/GetMyClubs/${params}`);
  }

  getClubPreviewById(clubId: string): Observable<TServiceResult<IClubPreviewModel>> {
    return this.http.get<TServiceResult<IClubPreviewModel>>(`${this.apiUrl}/GetClubPreviewById/${clubId}`);
  }

  getGuestClubs(term: string, filters: TermFilterModel): Observable<TServiceResult<PageList<IClubWithActiveCourseModel>>> {
    const params = this.ServiceTermFilter(filters);
    return this.http.get<TServiceResult<PageList<IClubWithActiveCourseModel>>>(`${this.apiUrl}/GetGuestClubs/${term}/${params}`)
      .pipe(
        catchError(err => {
          return of(new TServiceResult<PageList<IClubWithActiveCourseModel>>(new PageList(), '', null, true, ''))
        })
      )
  }

  getChosenClubs(): Observable<TServiceResult<PageList<IClubWithActiveCourseModel>>> {
    return this.http.post<TServiceResult<PageList<IClubWithActiveCourseModel>>>(`${this.apiUrl}/GetChosenClubs`, {})
  }

  getChosenClubsWithState(searchFilter?: SearchFilterModel): Observable<TServiceResult<PageList<IClubWithActiveCourseModel>>> {
    return this.http.post<TServiceResult<PageList<IClubWithActiveCourseModel>>>(`${this.apiUrl}/GetChosenClubsWithState`, searchFilter)
  }

  getClubMembers(clubId: string, filters: TermFilterModel): Observable<TServiceResult<PageList<IClubMemberAggregateModel>>> {
    const params = this.ServiceTermFilter(filters);
    return this.http.get<TServiceResult<PageList<IClubMemberAggregateModel>>>(`${this.apiUrl}/GetClubMembers/${clubId}/${params}`);
  }

  getCustomerForInviteByFilter(clubId: string, filters: SearchFilterModel): Observable<TServiceResult<PageList<IClubCustomerModel>>> {
    return this.http.post<TServiceResult<PageList<IClubCustomerModel>>>(`${this.apiUrl}/GetCustomerForInviteByFilter/${clubId}`, filters);
  }

  getClubJoinRequests(clubId: string, filters: TermFilterModel): Observable<TServiceResult<PageList<IClubJoinRequestAggregateModel>>> {
    const params = this.ServiceTermFilter(filters)
    return this.http.get<TServiceResult<PageList<IClubJoinRequestAggregateModel>>>(`${this.apiUrl}/GetClubJoinRequests/${clubId}/${params}`);
  }

  getClubInvites(clubId: string, filters: TermFilterModel): Observable<TServiceResult<PageList<IClubInviteAggregateModel>>> {
    return this.http.post<TServiceResult<PageList<IClubInviteAggregateModel>>>(`${this.apiUrl}/GetClubInvites/${clubId}`, filters);
  }

  getCourseById(courseId: string): Observable<TServiceResult<IClubCourseAggregateModel>> {
    return this.http.get<TServiceResult<IClubCourseAggregateModel>>(`${this.apiUrl}/GetCourseById/${courseId}`);
  }

  getClubCourses(clubId: string, filters: TermFilterModel): Observable<TServiceResult<PageList<IClubCourseAggregateModel>>> {
    return this.http.post<TServiceResult<PageList<IClubCourseAggregateModel>>>(`${this.apiUrl}/GetClubCourses/${clubId}`, filters);
  }

  getCoursesByBookId(bookId: string, filters: TermFilterModel): Observable<TServiceResult<PageList<IClubCourseAggregateModel>>> {
    return this.http.post<TServiceResult<PageList<IClubCourseAggregateModel>>>(`${this.apiUrl}/GetCoursesByBookId/${bookId}`, filters);
  }

  getClubPolls(clubId: string, filters: TermFilterModel, state?: EClubPollState): Observable<TServiceResult<PageList<IClubPollAggregateModel>>> {
    return this.http.post<TServiceResult<PageList<IClubPollAggregateModel>>>(
      `${this.apiUrl}/GetClubPolls/${clubId}` + ((state != null) ? `?state=${state}` : ''),
      filters
    );
  }

  getPollById(pollId: string): Observable<TServiceResult<IClubPollAggregateModel>> {
    return this.http.get<TServiceResult<IClubPollAggregateModel>>(`${this.apiUrl}/GetPollById/${pollId}`);
  }
}

